import { Toaster } from "@/components/ui/toaster";
import React from "react";
import { Navigate, Route, Routes, useLocation, matchPath } from "react-router-dom";
import { TOP_HOST } from "./api/_client";
import { useUser } from "./api/user";
import AgentSession from "./components/agent";
import { ProjectChatPanel } from "./components/projects/project-chat-panel";
import { GenericErrorComponent } from "./components/error-boundary";
import LoadingSpinner from "./components/loading-spinner";
import { SessionTimeControl } from "./components/session-control";
import { Sidebar } from "./components/sidebar";
import { Confirmation } from "./components/ui/confirmation";
import {
  type AgentViewDescription,
  useAgentContext,
} from "./providers/agent-provider";
import { AgentProvider } from "./providers/agent-provider.component";
import { useAppContext } from "./providers/app-context";
import { UploadProvider } from "./providers/upload-provider.component";
import { ROUTES } from "./routes";
import PasswordReset from "./views/password-reset";
import SignInView from "./views/sign-in";
import SignUpView from "./views/sign-up";
import VerifyEmail from "./views/verify-email";

const GlobalHome = React.lazy(() => import("./views/global-home"));
const AdminHomeAgentEntry = React.lazy(
  () => import("./components/global-home/admin-home-agent-entry"),
);

// Find the matching route (including children) for a given pathname
function findMatchingRoute(pathname: string) {
  for (const route of ROUTES) {
    if (route.children) {
      const basePath = route.match
        ? route.match.replace("/*", "")
        : route.path.replace(/\/$/, "");

      for (const child of route.children) {
        const fullChildPath = basePath + child.path;
        const match = matchPath(fullChildPath, pathname);
        if (match) {
          return child;
        }
      }

      if (pathname.startsWith(basePath)) {
        return route;
      }
    } else {
      if (route.match) {
        const basePath = route.match.replace("/*", "");
        if (pathname.startsWith(basePath)) {
          return route;
        }
      } else if (pathname === route.path) {
        return route;
      }
    }
  }
  return null;
}

/** Always syncs the route-derived view description into AgentProvider context. */
function AgentViewSync({
  description,
}: {
  description: AgentViewDescription | undefined;
}) {
  const { setCurrentViewDescription } = useAgentContext();
  React.useEffect(() => {
    setCurrentViewDescription(description ?? null);
  }, [setCurrentViewDescription, description]);
  return null;
}

function App() {
  const {
    initialized,
    collapseSidebar,
    showAbsoluteSpinner,
    actingOrganization,
    globalError,
    showMobileSidebar,
    setShowMobileSidebar,
    hideSidebar,
    setHideSidebar,
    isAgentMinimized,
    setIsAgentMinimized,
    embeddedChatActive,
  } = useAppContext();
  const user = useUser();
  const location = useLocation();
  const [hideAgent, setHideAgent] = React.useState(false);

  // Helper function to extract params from pathname based on route pattern
  const extractParamsFromPath = (
    pathname: string,
    route: { path?: string } | null,
  ) => {
    if (!route?.path) return {};

    let routePath = route.path;

    // Build the full path for child routes
    const parentRoute = ROUTES.find(r => {
      if (r.match) {
        const basePath = r.match.replace("/*", "");
        return pathname.startsWith(basePath);
      }
      return false;
    });

    if (parentRoute && parentRoute.children) {
      const basePath = parentRoute.match
        ? parentRoute.match.replace("/*", "")
        : parentRoute.path.replace(/\/$/, "");
      routePath = basePath + routePath;
    }


    // Use matchPath to extract params
    const match = matchPath(routePath, pathname);

    return match?.params || {};
  };
  const currentRoute = React.useMemo(() => {
    return findMatchingRoute(location.pathname) || null;
  }, [location.pathname]);

  const onTopHost = TOP_HOST === window.location.host;

  const currentViewDescription = React.useMemo<
    AgentViewDescription | undefined
  >(() => {
    // TOP_HOST description is owned by <AdminHomeAgentContext> below so
    // it can include the user's org list (which needs useGlobalDashboard).
    if (onTopHost) return undefined;
    const routeWithDescription = currentRoute as {
      description?: (...args: unknown[]) => AgentViewDescription;
    };
    return routeWithDescription?.description?.(
      extractParamsFromPath(location.pathname, currentRoute),
    );
  }, [currentRoute, location.pathname, onTopHost]);

  React.useEffect(() => {
    if (TOP_HOST === window.location.host) {
      setHideSidebar(true);
    }
  }, [user.data, setHideSidebar]);

  React.useEffect(() => {
    if (showMobileSidebar) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => document.body.classList.remove("overflow-hidden");
  }, [showMobileSidebar]);

  React.useEffect(() => {
    const unverified = user.data && user.data?.email_verified === false;
    setHideAgent(!!unverified);
  }, [user.data, location.pathname]);

  if (globalError) return <GenericErrorComponent />;
  if (!initialized) return <LoadingSpinner delay={false} />;

  return (
    <div className="flex flex-row overflow-x-hidden">
      {!hideSidebar && (
        <>
          {/* mobile sidebar */}
          <div
            className={`absolute z-[51] block transition-all lg:hidden ${
              showMobileSidebar ? "left-0" : "left-[-260px]"
            } lg:left-0`}
          >
            {user.data && <Sidebar className={`w-[260px]`} collapsed={false} />}
          </div>
          {/* mobile sidebar backdrop */}
          <div
            className={`fixed left-0 top-0 z-50 h-full w-full bg-black opacity-50 lg:z-[-50] lg:hidden ${
              !showMobileSidebar && "hidden"
            }`}
            onClick={() => setShowMobileSidebar((prev: boolean) => !prev)}
          />
          {/* Regular sidebar */}
          <div className="absolute left-[-260px] transition-all lg:left-0">
            {user.data && (
              <Sidebar
                className={`${
                  collapseSidebar ? "w-[62px]" : "w-[260px]"
                } transition-all`}
                collapsed={collapseSidebar}
              />
            )}
          </div>
        </>
      )}
      <div
        className={`${
          hideSidebar
            ? "w-full"
            : user.data
              ? collapseSidebar
                ? "w-full lg:ml-[62px] lg:w-[calc(100vw-62px)]"
                : "w-full lg:ml-[260px] lg:w-[calc(100vw-260px)]"
              : "w-full lg:ml-0"
        } transition-all`}
      >
        <React.Suspense fallback={<LoadingSpinner />}>
          <>
            {user.data && user.data?.email_verified === false ? (
              <Routes>
                <Route path="/verify/:uid/:token" element={<VerifyEmail />} />
                <Route path="*" element={<VerifyEmail />} />
              </Routes>
            ) : user.data ? (
              <UploadProvider>
                <AgentProvider>
                  <Routes>
                    {TOP_HOST === window.location.host ? (
                      <>
                        <Route path="*" element={<GlobalHome />} />
                      </>
                    ) : (
                      ROUTES.map((route) => {
                        if (
                          actingOrganization &&
                          route.canAccess &&
                          !route.canAccess(actingOrganization!)
                        ) {
                          return null;
                        }
                        if (route.children) {
                          return (
                            <Route
                              path={route.match}
                              key={route.name}
                              element={
                                <Routes>
                                  <Route path="/" element={<route.component />} />
                                  {route.children?.map((child) => (
                                    <Route
                                      path={child.path}
                                      element={<child.component />}
                                      key={route.path + child.path}
                                    />
                                  ))}
                                </Routes>
                              }
                            />
                          );
                        }
                        return (
                          <Route
                            path={route.path}
                            element={<route.component />}
                            key={route.name}
                          />
                        );
                      })
                    )}
                    <Route path="/verify/:uid/:token" element={<VerifyEmail />} />
                    <Route path="/error" element={<GenericErrorComponent />} />
                    <Route
                      path="*"
                      element={<Navigate to="/" replace />}
                    />
                  </Routes>
                  <AgentViewSync description={currentViewDescription} />
                  {!hideAgent && !embeddedChatActive && (
                    onTopHost ? (
                      <AdminHomeAgentEntry
                        isMinimized={isAgentMinimized}
                        onMinimizedChange={setIsAgentMinimized}
                      />
                    ) : (
                      <AgentSession
                        currentViewDescription={currentViewDescription}
                        isMinimized={isAgentMinimized}
                        onMinimizedChange={setIsAgentMinimized}
                      />
                    )
                  )}
                  <ProjectChatPanel />
                </AgentProvider>
              </UploadProvider>
            ) : (
              <Routes>
                <Route
                  path="/reset-password/:uidb64/:token"
                  element={<PasswordReset />}
                />
                <Route path="/join" element={<SignUpView />} />
                <Route path="/error" element={<GenericErrorComponent />} />
                <Route path="*" element={<SignInView />} />
              </Routes>
            )}
          </>
        </React.Suspense>
      </div>
      <Toaster />
      <Confirmation />
      <SessionTimeControl />

      {showAbsoluteSpinner && <LoadingSpinner absolute />}
    </div>
  );
}

export default App;
